import { useCallback, useMemo } from 'react'
import {
  isResourceOfType,
  useReadPlanningStatuses,
  useReadServices,
} from '@opallabs/api'
import { type AllStatuses } from '@opallabs/concepts'
import {
  CALENDAR_CONTENT_STATUS_MARKS,
  CALENDAR_MOMENT_SUMMARY_MARKS,
  type CalendarFilterCategory,
  type CalendarFilterOption,
  type CalendarWorkflowSummaries,
  CalendarFilterCategoryIds,
  workflowSummaryKey,
} from '@opallabs/filters'
import { Size } from '@opallabs/gem'
import { useOnceTrue } from '@opallabs/hooks'
import { WorkflowIcon } from '@opallabs/lapidary'
import { ChannelIcon } from '@opallabs/satellite'
import { CalendarSizes } from '../sizes'
import {
  type CalendarDisplayField,
  type CalendarItem,
  type ResolveDisplay,
} from '../types'

/** What a channel's artwork is drawn from, as `/services/v2` describes it. */
export type ChannelMark = Readonly<{
  color?: string
  iconName?: string
  imageSrc?: string
  name: string
  systemName: string
}>

export type ServicesDocument = Parameters<
  NonNullable<Parameters<typeof useReadServices>[0]['select']>
>[0]

type ServiceResource = ServicesDocument['data'][number]
type AssetResource = Extract<
  NonNullable<ServicesDocument['included']>[number],
  { type: 'asset_reference' }
>

/** A service's artwork, with the picture the service's asset reference names. */
export const channelMarkOf = (
  service: ServiceResource,
  assets: readonly AssetResource[]
): ChannelMark => {
  const asset = assets.find(
    (each) => each.id === service.relationships.asset_reference.data?.id
  )

  return {
    color: service.attributes.color ?? undefined,
    iconName: service.attributes.icon_name ?? undefined,
    imageSrc: asset?.attributes.preview_url ?? undefined,
    name: service.attributes.name,
    systemName: service.attributes.system_name,
  }
}

/** The channel's artwork, at the size the place it is drawn in asks for. */
export const drawChannelMark = (channel: ChannelMark, iconSize?: number) => (
  <ChannelIcon
    channelColor={channel.color}
    channelIconName={channel.iconName}
    channelName={channel.name}
    channelSystemName={channel.systemName}
    iconSize={iconSize}
    imageSrc={channel.imageSrc}
    privateForUser={false}
  />
)

type NameLookups = Readonly<
  Record<Exclude<CalendarDisplayField, 'moment'>, Map<string, string>>
>

const labelsById = (
  categories: readonly CalendarFilterCategory[],
  ...categoryIds: string[]
): Map<string, string> => {
  const lookup = new Map<string, string>()

  categoryIds.forEach((categoryId) => {
    categories
      .find((category) => category.id === categoryId)
      ?.options.forEach((option) => {
        lookup.set(option.id, option.label)

        // An option whose id is not the value an item carries answers to that
        // value as well: a content item says `approved`, while the status list
        // offers it as `content.approved` so the moment word of the same name
        // stays a filter of its own. A row that stands for other rows is left
        // out — it names no value of its own, and would take their names.
        if (option.group?.id === option.id) return
        Object.values(option.values ?? {}).forEach((values) =>
          (values ?? []).forEach((value) => lookup.set(value, option.label))
        )
      })
  })

  return lookup
}

/**
 * The set each label sits in, named, keyed by the label. A set is offered as
 * an option too, grouped under itself, and is no label of anyone's.
 */
const labelSetsByLabel = (
  categories: readonly CalendarFilterCategory[]
): Map<string, string> =>
  new Map(
    (
      categories.find(
        (category) => category.id === CalendarFilterCategoryIds.label
      )?.options ?? []
    ).flatMap((option) =>
      option.group === undefined || option.group.id === option.id
        ? []
        : [[option.id, option.group.label] as [string, string]]
    )
  )

/**
 * The workspace's names, keyed the way calendar items are keyed.
 *
 * They come from the filter option lists rather than from fetches of their own.
 * Those lists already hold every account, channel, label, story, board and
 * status in the workspace under the uuid an item carries, so reading them here
 * means a name shown in a panel and a name offered in the drawer can never
 * disagree, and the calendar makes no extra request for the privilege.
 */
export const buildNameLookups = (
  categories: readonly CalendarFilterCategory[]
): NameLookups => {
  // Channels and accounts are offered as one list, and their uuids never
  // collide, so both fields read the same names.
  const channels = labelsById(categories, CalendarFilterCategoryIds.channels)

  return {
    account: channels,
    board: labelsById(categories, CalendarFilterCategoryIds.board),
    channel: channels,
    label: labelsById(categories, CalendarFilterCategoryIds.label),
    labelSet: labelSetsByLabel(categories),
    status: labelsById(categories, CalendarFilterCategoryIds.status),
    story: labelsById(categories, CalendarFilterCategoryIds.story),
  }
}

/**
 * The colours the workspace's filter options carry, keyed the way calendar
 * items are keyed.
 *
 * Only labels carry one today, and the ids of the other lists never collide
 * with a label's, so one map can answer whatever the calendar is grouped by. A
 * channel's colour is not here: it belongs to the channel's artwork, which the
 * options do not hold.
 */
export const buildColorLookup = (
  categories: readonly CalendarFilterCategory[]
): Map<string, string> =>
  new Map(
    categories.flatMap((category) =>
      category.options.flatMap((option) =>
        option.color === undefined
          ? []
          : [[option.id, option.color] as [string, string]]
      )
    )
  )

/**
 * The workspace's label sets, in the order the drawer offers them.
 *
 * A set is an option as well as the heading its labels sit under, which is what
 * tells the two apart: only a set names itself as its own group.
 */
export const buildLabelSets = (
  categories: readonly CalendarFilterCategory[]
): ReadonlyArray<{ id: string; label: string }> =>
  (
    categories.find(
      (category) => category.id === CalendarFilterCategoryIds.label
    )?.options ?? []
  )
    .filter((option) => option.group?.id === option.id)
    .map(({ id, label }) => ({ id, label }))

/**
 * The colour of every label in one set, with the place it holds in the set.
 *
 * An item can carry several labels from one set, and only one of them can
 * colour it, so the place decides: the first label of the set wins.
 */
export const buildLabelSetColors = (
  categories: readonly CalendarFilterCategory[],
  labelSetId: string | undefined
): ReadonlyMap<string, { color: string; position: number }> => {
  const lookup = new Map<string, { color: string; position: number }>()
  if (labelSetId === undefined) return lookup
  ;(
    categories.find(
      (category) => category.id === CalendarFilterCategoryIds.label
    )?.options ?? []
  )
    .filter(
      (option) => option.group?.id === labelSetId && option.id !== labelSetId
    )
    .forEach((option, position) => {
      if (option.color !== undefined)
        lookup.set(option.id, { color: option.color, position })
    })

  return lookup
}

/**
 * The mark an item has earned, or none.
 *
 * Moments and content answer to two systems. A moment's mark is the workflow
 * over it, so a moment under no workflow wears none, and none while the
 * workflows are still being read rather than one that is about to change.
 * Content's mark is its own approval status, which arrives with the month.
 * Content with no approval set up is also a draft, and the status cannot tell
 * it apart from a draft waiting to be sent, so no draft wears a mark. Week view
 * puts a "not sent" dot on every draft card instead.
 */
const statusMarkFor = (
  item: CalendarItem,
  summaries: CalendarWorkflowSummaries
): AllStatuses | undefined => {
  if (item.kind === 'content')
    return item.statusId === undefined || item.statusId === 'draft'
      ? undefined
      : CALENDAR_CONTENT_STATUS_MARKS[item.statusId]
  if (item.kind !== 'moment') return undefined

  const summary = summaries.byKey.get(workflowSummaryKey('moment', item.id))
  return summary === undefined
    ? undefined
    : CALENDAR_MOMENT_SUMMARY_MARKS[summary]
}

/**
 * The artwork for each channel, keyed by the uuid an item carries.
 *
 * Kept out of the hook so React Query is handed the same function every render:
 * a new one makes it build this map again for every service in the workspace,
 * every time anything on the calendar changes.
 */
const toChannelMarks = (
  document: ServicesDocument
): Map<string, ChannelMark> => {
  const assets = (document.included ?? []).filter(
    isResourceOfType('asset_reference', 'V2')
  )

  return new Map(
    document.data.flatMap((service) => {
      const uuid = service.attributes.uuid
      if (uuid === undefined || uuid === null) return []

      return [[uuid, channelMarkOf(service, assets)] as [string, ChannelMark]]
    })
  )
}

/**
 * The names of the workspace's planning statuses, keyed by the id a moment
 * carries.
 *
 * Planning statuses are no longer something a person can filter by, so this is
 * the only reason the calendar reads them: naming the status in the panel. A
 * workflow status and a planning status never share an id, so one lookup can
 * answer for both kinds without being told which is asking.
 */
const toPlanningStatusNames = (
  data: Parameters<
    NonNullable<
      NonNullable<Parameters<typeof useReadPlanningStatuses>[0]>['select']
    >
  >[0]
): Map<string, string> =>
  new Map(
    data.data.map((status) => [status.id, status.attributes.display_name])
  )

export type UseWorkspaceDisplayResult = Readonly<{
  /** The `card` variant is drawn smaller, to sit on a card's detail lines. */
  renderChannelIcon: (
    item: CalendarItem,
    variant?: 'card' | 'field'
  ) => React.ReactNode
  /**
   * The colour an item carries in the label set the month is coloured by, or
   * `undefined` when it carries no label in that set — or when no set was
   * chosen at all.
   */
  resolveChosenColor: (item: CalendarItem) => string | undefined
  /**
   * The colour a group of items is drawn in — a channel's own, a label's, or a
   * moment's. Accounts have none, and a swatch in the wrong colour says less
   * than none.
   */
  resolveColor: (groupId: string) => string | undefined
  /** For the filter drawer: the mark of any option that names a channel. */
  renderOptionIcon: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
  renderStatusIcon: (item: CalendarItem, size: number) => React.ReactNode
  resolveDisplay: ResolveDisplay
}>

/**
 * Turns the ids on a calendar item into something a person can read, and draws
 * the channel mark on a content chip.
 *
 * Channel marks are the one thing the filter options cannot supply: drawing one
 * needs artwork and a system name, which an option does not carry.
 */
export const useWorkspaceDisplay = ({
  colors,
  labelSetColors,
  items,
  names,
  shouldFetchPlanningStatuses,
  workflowSummaries,
  workspaceId,
}: {
  colors: ReadonlyMap<string, string>
  /** The chosen label set's colours, from `buildLabelSetColors`. Empty means none. */
  labelSetColors: ReadonlyMap<string, { color: string; position: number }>
  /** Everything on the calendar, for naming a content item's parent moment. */
  items: readonly CalendarItem[]
  names: NameLookups
  /** Only a panel names a planning status, so the list waits for one to open. */
  shouldFetchPlanningStatuses: boolean
  /** The workflow over each moment, which is what a workflow mark stands for. */
  workflowSummaries: CalendarWorkflowSummaries
  /** The numeric workspace id the v2 services endpoint takes. */
  workspaceId: string
}): UseWorkspaceDisplayResult => {
  const { data: channels } = useReadServices({
    select: toChannelMarks,
    workspaceId,
  })

  const { data: planningStatuses } = useReadPlanningStatuses({
    enabled: useOnceTrue(shouldFetchPlanningStatuses),
    select: toPlanningStatusNames,
  })

  const momentTitles = useMemo(
    () =>
      new Map(
        items
          .filter((item) => item.kind === 'moment')
          .map((item) => [item.id, item.title])
      ),
    [items]
  )

  // A month sorted by moment splits the day summary by moment, and a moment
  // is neither a channel nor a label, so its colour is nowhere in the filter
  // options. It is on the moment itself.
  const momentColors = useMemo(
    () =>
      new Map(
        items.flatMap((item) =>
          item.kind === 'moment' && item.primaryColor !== undefined
            ? [[item.id, item.primaryColor] as [string, string]]
            : []
        )
      ),
    [items]
  )

  // Anything keyed by a channel uuid can wear the channel's mark: a content
  // chip, a channel option in the drawer, or the heading its accounts sit under.
  const renderChannelMark = useCallback(
    (channelId?: string, iconSize?: number) => {
      const channel =
        channelId === undefined ? undefined : channels?.get(channelId)
      if (channel === undefined) return null

      return drawChannelMark(channel, iconSize)
    },
    [channels]
  )

  return useMemo(
    () => ({
      renderChannelIcon: (item: CalendarItem, variant?: 'card' | 'field') =>
        renderChannelMark(
          item.channelId,
          variant === 'card'
            ? CalendarSizes.cardDetailIconSize
            : variant === 'field'
              ? Size.Icon
              : CalendarSizes.chipIconSize
        ),
      renderOptionIcon: (
        _category: CalendarFilterCategory,
        option: CalendarFilterOption
      ) => renderChannelMark(option.id, Size.Icon),
      renderStatusIcon: (item: CalendarItem, size: number) => {
        const status = statusMarkFor(item, workflowSummaries)
        if (status === undefined) return null

        return <WorkflowIcon size={size} status={status} />
      },
      resolveChosenColor: (item: CalendarItem) => {
        let best: { color: string; position: number } | undefined
        item.labelIds?.forEach((labelId) => {
          const found = labelSetColors.get(labelId)
          if (
            found !== undefined &&
            (best === undefined || found.position < best.position)
          )
            best = found
        })

        return best?.color
      },
      resolveColor: (groupId) =>
        channels?.get(groupId)?.color ??
        colors.get(groupId) ??
        momentColors.get(groupId),
      resolveDisplay: (field, id) => {
        if (field === 'moment') return momentTitles.get(id)
        if (field === 'status') {
          return names.status.get(id) ?? planningStatuses?.get(id)
        }
        return names[field].get(id)
      },
    }),
    [
      channels,
      colors,
      labelSetColors,
      momentColors,
      momentTitles,
      names,
      planningStatuses,
      renderChannelMark,
      workflowSummaries,
    ]
  )
}
